import React from "react";

import { Badge, type badgeVariants } from "@/components/ui/badge";
import type { VariantProps } from "class-variance-authority";
import type {
  ExitHookReadiness,
  InjectReadiness,
  ReportStrategyPineFields,
} from "../../../types/reportStrategyPine";
import type { ReportStrategyResponse } from "../../../types/reports";
import {
  getExitHookReadinessDisplay,
  getInjectReadinessDisplay,
  getParseStatusDisplay,
  getPineScriptKindDisplay,
  shouldShowInjectReadinessBadge,
} from "../../../utils/pineIrUi";
import { mergeStrategyWithParseSummary } from "../../../utils/strategyComposeUi";
import {
  activePineStatusLabel,
  extractPineScriptText,
  formatActivePineProvenanceBadgeTitle,
  formatExitHookReadinessLabel,
  formatInjectReadinessLabel,
  formatPineParseStatusLabel,
  formatPineScriptKindLabel,
  PINE_SCRIPT_CREATED_LABEL,
  PINE_SCRIPT_NOT_CREATED_LABEL,
  PINE_SCRIPT_NOT_UPLOADED_LABEL,
  PINE_SCRIPT_UPLOADED_LABEL,
  shouldShowIndicatorOverlayArtifact,
  strategyHasMasterPinescriptUploaded,
  strategyHasPineSignals,
  strategyHasTradingViewPinescriptCreated,
} from "../../../utils/reportStrategyPineUi";
import type { StrategyActivePineSummary } from "../../../types/reportStrategyPine";

export type StrategyPineBadgeVariant = NonNullable<
  VariantProps<typeof badgeVariants>["variant"]
>;

type PineBadgeItem = {
  id: string;
  variant: StrategyPineBadgeVariant;
  label: string;
  title?: string;
  pineBadgeKind: string;
};

export const STRATEGY_PINE_BADGE_CLASS =
  "rounded-[6px] font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal shadow-none";

const BADGE_CLASS = STRATEGY_PINE_BADGE_CLASS;

const STRATEGY_OVERVIEW_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

const STRATEGY_OVERVIEW_FIELD_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white";

function parseBadge(strategy: ReportStrategyResponse): PineBadgeItem {
  const status = strategy.pine_parse_status!;
  const ux = getParseStatusDisplay(status);

  let variant: StrategyPineBadgeVariant = "pine-parse-none";
  if (status === "full") variant = "pine-parse-success";
  else if (status === "partial") variant = "pine-parse-partial";
  else if (status === "failed") variant = "pine-parse-failed";

  return {
    id: "parse",
    variant,
    label: formatPineParseStatusLabel(status),
    title: ux?.description,
    pineBadgeKind: `parse-${status}`,
  };
}

export function injectReadinessPineBadgeItem(
  readiness: InjectReadiness | string
): PineBadgeItem | null {
  if (!shouldShowInjectReadinessBadge(readiness)) return null;

  const ux = getInjectReadinessDisplay(readiness);

  if (readiness === "ready") {
    return {
      id: "inject",
      variant: "pine-status-ready-for-merge",
      label: ux?.shortLabel ?? "Ready for merge",
      title: ux?.description,
      pineBadgeKind: "status-ready-for-merge",
    };
  }

  let variant: StrategyPineBadgeVariant = "pine-inject-none";
  if (readiness === "needs_review") variant = "pine-inject-review";
  else if (readiness === "unsupported") variant = "pine-inject-unsupported";
  else if (readiness === "not_applicable") variant = "pine-inject-na";

  return {
    id: "inject",
    variant,
    label: ux?.shortLabel ?? formatInjectReadinessLabel(readiness),
    title: ux?.description,
    pineBadgeKind: `inject-${readiness}`,
  };
}

export function scriptKindPineBadgeItem(kind?: string | null): PineBadgeItem | null {
  if (!kind?.trim()) return null;
  const ux = getPineScriptKindDisplay(kind);
  return {
    id: "script-kind",
    variant: "pine-script-kind",
    label: formatPineScriptKindLabel(kind),
    title: ux?.description,
    pineBadgeKind: "script-kind",
  };
}

function injectBadge(strategy: ReportStrategyResponse): PineBadgeItem {
  return injectReadinessPineBadgeItem(strategy.inject_readiness!)!;
}

export function exitHookReadinessPineBadgeItem(
  readiness: ExitHookReadiness | string
): PineBadgeItem {
  let variant: StrategyPineBadgeVariant = "pine-exit-none";
  if (readiness === "ready") variant = "pine-exit-ready";
  else if (readiness === "partial") variant = "pine-exit-partial";
  else if (readiness === "unsupported") variant = "pine-exit-unsupported";
  else if (readiness === "not_applicable") variant = "pine-exit-na";

  const ux = getExitHookReadinessDisplay(readiness);
  return {
    id: "exit-hook",
    variant,
    label: formatExitHookReadinessLabel(readiness),
    title: ux?.description,
    pineBadgeKind: `exit-${readiness}`,
  };
}

function exitHookBadge(strategy: ReportStrategyResponse): PineBadgeItem {
  return exitHookReadinessPineBadgeItem(strategy.exit_hook_readiness!);
}

/** Pine status badges for strategy detail overview — canonical labels + variants. */
export function buildStrategyPineStatusBadgeItems(
  strategy: ReportStrategyResponse,
  options?: { excludeActiveComposeStatus?: boolean }
): PineBadgeItem[] {
  /** GET detail often stores parse readiness on pine_parse_summary, not root fields. */
  const merged = mergeStrategyWithParseSummary(strategy);
  const items: PineBadgeItem[] = [];
  const hasScript = strategyHasMasterPinescriptUploaded(merged);
  const composeStatus = merged.active_pine?.status;

  if (!options?.excludeActiveComposeStatus) {
    if (composeStatus === "composed") {
      items.push({
        id: "active-compose",
        variant: "pine-status-active-compose",
        label: PINE_SCRIPT_CREATED_LABEL,
        title: formatActivePineProvenanceBadgeTitle(merged.active_pine),
        pineBadgeKind: "status-active-compose",
      });
    } else if (composeStatus === "stale") {
      items.push({
        id: "active-compose",
        variant: "pine-compose-stale",
        label: "Compose outdated",
        title: formatActivePineProvenanceBadgeTitle(merged.active_pine),
        pineBadgeKind: "compose-stale",
      });
    }
  }

  if (!hasScript) {
    if (strategyHasPineSignals(merged)) {
      items.push({
        id: "signals-only",
        variant: "pine-status-signals-only",
        label: "Signals only",
        title: "Parse or readiness metadata without master script text",
        pineBadgeKind: "status-signals-only",
      });
    } else {
      items.push({
        id: "no-script",
        variant: "pine-status-no-script",
        label: "No script",
        title: "No Pinescript on this strategy",
        pineBadgeKind: "status-no-script",
      });
    }
  }

  if (!merged.pine_parse_status) {
    items.push({
      id: "not-parsed",
      variant: "pine-status-not-parsed",
      label: "Not parsed",
      title: "Pinescript has not been parsed for this strategy",
      pineBadgeKind: "status-not-parsed",
    });
  } else {
    items.push(parseBadge(merged));
  }

  if (!merged.inject_readiness) {
    items.push({
      id: "inject-unknown",
      variant: "pine-status-inject-unknown",
      label: "Inject unknown",
      title: "Inject readiness not evaluated",
      pineBadgeKind: "status-inject-unknown",
    });
  } else if (shouldShowInjectReadinessBadge(merged.inject_readiness)) {
    items.push(injectBadge(merged));
  }

  if (!merged.exit_hook_readiness) {
    items.push({
      id: "exit-hooks-unknown",
      variant: "pine-status-exit-hooks-unknown",
      label: "Exit hooks unknown",
      title: "Exit hook readiness not evaluated",
      pineBadgeKind: "status-exit-hooks-unknown",
    });
  } else {
    items.push(exitHookBadge(merged));
  }

  if (shouldShowIndicatorOverlayArtifact(merged)) {
    items.push({
      id: "indicator-overlay",
      variant: "pine-status-indicator-overlay",
      label: "Indicator overlay saved",
      title: "Indicator overlay Pinescript saved on this strategy",
      pineBadgeKind: "status-indicator-overlay",
    });
  }

  return items;
}

/** Accordion header — active / stale / none compose status. */
export function activePineAccordionHeaderBadge(
  active?: StrategyActivePineSummary | null
): PineBadgeItem | null {
  const status = active?.status;
  if (!status) return null;
  const label = activePineStatusLabel(active);
  if (!label) return null;
  const title = formatActivePineProvenanceBadgeTitle(active);
  if (status === "composed") {
    return {
      id: "header-compose",
      variant: "pine-status-active-compose",
      label,
      title,
      pineBadgeKind: "status-active-compose",
    };
  }
  if (status === "stale") {
    return {
      id: "header-compose",
      variant: "pine-compose-stale",
      label,
      title,
      pineBadgeKind: "compose-stale",
    };
  }
  return {
    id: "header-compose",
    variant: "pine-compose-none",
    label,
    title,
    pineBadgeKind: "compose-none",
  };
}

/** Parse / inject / exit / script kind chips (editor, legacy status row). */
export function buildStrategyPineEditorBadgeItems(
  fields: ReportStrategyPineFields
): PineBadgeItem[] {
  const items: PineBadgeItem[] = [];
  const strategy = mergeStrategyWithParseSummary(fields as ReportStrategyResponse);

  if (fields.pine_script_kind) {
    const scriptKind = scriptKindPineBadgeItem(fields.pine_script_kind);
    if (scriptKind) items.push(scriptKind);
  }
  if (fields.pine_parse_status) {
    items.push(parseBadge(strategy));
  }
  if (shouldShowInjectReadinessBadge(fields.inject_readiness)) {
    items.push(injectBadge(strategy));
  }
  if (fields.exit_hook_readiness) {
    items.push(exitHookBadge(strategy));
  }
  if (fields.has_composed_pine) {
    items.push({
      id: "composed-saved",
      variant: "pine-meta",
      label: "Composed strategy",
      pineBadgeKind: "meta-composed",
    });
  }
  if (shouldShowIndicatorOverlayArtifact(fields)) {
    items.push({
      id: "indicator-overlay",
      variant: "pine-status-indicator-overlay",
      label: "Indicator overlay saved",
      pineBadgeKind: "status-indicator-overlay",
    });
  }
  return items;
}

export function PineBadgeList({ items }: { items: PineBadgeItem[] }): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {items.map((item) => (
        <Badge
          key={item.id}
          variant={item.variant}
          data-strategies-pine-badge={item.pineBadgeKind}
          className={BADGE_CLASS}
          title={item.title}
        >
          {item.label}
        </Badge>
      ))}
    </div>
  );
}

function StrategyTablePineBadgeCell({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div data-strategies-table-pine-cell="" className="flex items-center">
      {children}
    </div>
  );
}

/** Strategies list table — master Pinescript uploaded / not uploaded. */
export function StrategyTableMasterPineBadge({
  strategy,
}: {
  strategy: ReportStrategyResponse;
}): React.ReactElement {
  const uploaded = strategyHasMasterPinescriptUploaded(strategy);
  const signalsOnly = !uploaded && strategyHasPineSignals(strategy);
  const title = uploaded
    ? "Master Pinescript uploaded on this strategy"
    : signalsOnly
      ? "Signals metadata without uploaded master Pinescript"
      : "No master Pinescript uploaded on this strategy";

  if (signalsOnly) {
    return (
      <StrategyTablePineBadgeCell>
        <Badge
          variant="pine-status-signals-only"
          data-strategies-pine-badge="status-signals-only"
          className={BADGE_CLASS}
          title={title}
        >
          Signals only
        </Badge>
      </StrategyTablePineBadgeCell>
    );
  }

  return (
    <StrategyTablePineBadgeCell>
      <Badge
        variant={uploaded ? "pine-master-script" : "pine-master-none"}
        data-strategies-pine-badge={uploaded ? "master-uploaded" : "master-not-uploaded"}
        className={BADGE_CLASS}
        title={title}
      >
        {uploaded ? PINE_SCRIPT_UPLOADED_LABEL : PINE_SCRIPT_NOT_UPLOADED_LABEL}
      </Badge>
    </StrategyTablePineBadgeCell>
  );
}

/** Strategies list table — composed TradingView Pinescript created / not created. */
export function StrategyTableTradingViewPineBadge({
  strategy,
}: {
  strategy: ReportStrategyResponse;
}): React.ReactElement {
  const created = strategyHasTradingViewPinescriptCreated(strategy);
  const stale = strategy.active_pine?.status === "stale";
  const title = formatActivePineProvenanceBadgeTitle(strategy.active_pine);

  if (created) {
    return (
      <StrategyTablePineBadgeCell>
        <Badge
          variant={stale ? "pine-compose-stale" : "pine-compose-active"}
          data-strategies-pine-badge={stale ? "compose-stale" : "compose-created"}
          className={BADGE_CLASS}
          title={
            stale
              ? `${title ?? "Composed TradingView Pinescript"}. Master Pinescript changed. Re-compose to refresh.`
              : title
          }
        >
          {PINE_SCRIPT_CREATED_LABEL}
        </Badge>
      </StrategyTablePineBadgeCell>
    );
  }

  return (
    <StrategyTablePineBadgeCell>
      <Badge
        variant="pine-compose-none"
        data-strategies-pine-badge="compose-not-created"
        className={BADGE_CLASS}
        title={title ?? "No composed TradingView Pinescript yet"}
      >
        {PINE_SCRIPT_NOT_CREATED_LABEL}
      </Badge>
    </StrategyTablePineBadgeCell>
  );
}

/** @deprecated Use StrategyTableTradingViewPineBadge */
export function StrategyTableActivePineBadge({
  strategy,
}: {
  strategy: ReportStrategyResponse;
}): React.ReactElement {
  return <StrategyTableTradingViewPineBadge strategy={strategy} />;
}

/** @deprecated Use StrategyTableTradingViewPineBadge */
export const StrategyTablePineBadges = StrategyTableActivePineBadge;
